/**
 * Resize + re-encode ảnh về kích thước hợp lý để AI vision API xử lý được.
 *
 * Lý do:
 *   - Ảnh từ camera/screenshot thường 3-10MB (base64 còn to hơn ~33%).
 *   - DS2API proxy hay strip payload quá lớn → AI nhận được placeholder
 *     rỗng "[omitted_binary_payload]" thay vì ảnh thật.
 *
 * Chiến lược:
 *   - Resize longest edge xuống `maxDim` (default 1600) — đủ rõ cho OCR/vision
 *   - Convert sang JPEG quality 0.85 (hoặc PNG nếu ảnh có alpha)
 *   - Giữ nguyên nếu ảnh đã đủ nhỏ
 *
 * @param {File} file
 * @param {Object} opts
 * @param {number} opts.maxDim       - cạnh dài tối đa (default 1600)
 * @param {number} opts.maxBytes     - nếu file gốc < ngưỡng này thì giữ nguyên (default 500KB)
 * @param {number} opts.quality      - JPEG quality 0..1 (default 0.85)
 * @returns {Promise<{ base64Url: string, mimeType: string, width: number, height: number, originalSize: number, newSize: number }>}
 */
export async function resizeImageFile(
    file,
    { maxDim = 1600, maxBytes = 500 * 1024, quality = 0.85 } = {},
) {
    if (!file || !file.type?.startsWith("image/")) {
        throw new Error("Không phải file ảnh");
    }

    const originalSize = file.size;

    // Ảnh đã nhỏ → skip resize, chỉ đọc base64
    if (originalSize <= maxBytes) {
        const base64Url = await fileToDataUrl(file);
        return {
            base64Url,
            mimeType: file.type,
            width: 0,
            height: 0,
            originalSize,
            newSize: originalSize,
        };
    }

    const img = await loadImage(file);

    const { width: w0, height: h0 } = img;
    const longest = Math.max(w0, h0);
    const scale = longest > maxDim ? maxDim / longest : 1;
    const w = Math.round(w0 * scale);
    const h = Math.round(h0 * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    // Quality cao khi downscale
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, w, h);

    // PNG có thể có alpha → giữ PNG nếu cần minh bạch, ngược lại JPEG cho gọn
    const keepPng = file.type === "image/png";
    const outType = keepPng ? "image/png" : "image/jpeg";

    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error("Không encode được ảnh"))),
            outType,
            outType === "image/jpeg" ? quality : undefined,
        );
    });

    const base64Url = await blobToDataUrl(blob);

    return {
        base64Url,
        mimeType: outType,
        width: w,
        height: h,
        originalSize,
        newSize: blob.size,
    };
}

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = (e) => {
            URL.revokeObjectURL(url);
            reject(e);
        };
        img.src = url;
    });
}

function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Format bytes → human readable
 */
export function humanSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
